import React, { useState, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";
import openNotification from "components/utils/CustomNotification";
import 'flatpickr/dist/themes/material_blue.css';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";
import { SessionExtend } from "components/utils/SessionCheckUtils";
import { withUIDirectives } from 'components/utils/UI_Directive/showUIDirectives';
import { TimePicker } from "antd";
import dayjs from "dayjs"; 
import type { Dayjs } from "dayjs";



interface AddNewsModalProps {
  onClose: () => void;
  onSuccess: () => void;
  initialData?: any;
  isEdit?: boolean;
}

const AddNewsModal: React.FC<AddNewsModalProps> = ({ onClose, onSuccess, initialData, isEdit }) => {
  useCheckSessionHeartBeat();
  const beAddress = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";
  const org_uid=localStorage.getItem("org_Uid") || "";

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imageBase64, setImageBase64] = useState("");
const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
const [toDate, setToDate] = useState<Date | undefined>(undefined);
const [formErrors, setFormErrors] = useState<{ title?: boolean; description?: boolean }>({});

const [fromTime, setFromTime] = useState<Dayjs | null>(null);
const [toTime, setToTime] = useState<Dayjs | null>(null);


  const [isSubmitting, setIsSubmitting] = useState(false);

const flatTimeToString = (date: Date | string): string => {
  if (!date) return "00:00";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toTimeString().slice(0, 5); 
};

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || "");
      setDescription(initialData.description || "");
      setFromDate(new Date(initialData.from_datetime));
setFromTime(dayjs(initialData.from_datetime));


setToDate(new Date(initialData.to_datetime));
setToTime(dayjs(initialData.to_datetime));


      setImageBase64(initialData.image || "");
    }
  }, [initialData]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageBase64(reader.result?.toString().split(",")[1] || "");
      };
      reader.readAsDataURL(file);
    }
  };

const handleSubmit = async () => {
  if (isSubmitting) return;
  setIsSubmitting(true);

  // Track which fields are missing
  const newErrors: { title?: boolean; description?: boolean } = {};
  if (!title.trim()) newErrors.title = true;
  if (!description.trim()) newErrors.description = true;

  if (!title.trim() || !description.trim() || !imageBase64 || !fromDate || !toDate) {
    setFormErrors(newErrors);
    openNotification({ type: "error", message: "Please fill all required fields." });
    setIsSubmitting(false);
    return;
  }

  // Clear previous field errors if everything is present
  setFormErrors({});

  // Validate character length
  if (title.length > 100 || description.length > 300) {
    openNotification({ type: "error", message: "Title or description is too long." });
    setIsSubmitting(false);
    return;
  }

  // Validate that start time is before end time
  if (new Date(`${fromDate.toDateString()} ${fromTime}`) > new Date(`${toDate.toDateString()} ${toTime}`)) {
    openNotification({ type: "error", message: "End date/time must be after start date/time." });
    setIsSubmitting(false);
    return;
  }

  // Format datetime in local format
  const formatLocalDateTime = (date: Date, time: string) =>
    `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}T${time}:00`;

const fromISOString = dayjs(fromDate)
  .hour(fromTime?.hour() || 0)
  .minute(fromTime?.minute() || 0)
  .second(0)
  .format("YYYY-MM-DDTHH:mm:ss");

const toISOString = dayjs(toDate)
  .hour(toTime?.hour() || 0)
  .minute(toTime?.minute() || 0)
  .second(0)
  .format("YYYY-MM-DDTHH:mm:ss");


  const endpoint = isEdit ? `/api/carousal/` : "/api/carousal";
  const method = isEdit ? axios.put : axios.post;

  try {
    const response = await method(
      `${beAddress}${endpoint}`,
      {
        org_uid: initialData?.org_uid || org_uid,
        image: imageBase64,
        from_datetime: fromISOString,
        to_datetime: toISOString,
        title: title.trim(),
        description: description.trim(),
        media_type: "image",
        ...(isEdit && { carousal_uid: initialData.carousal_uid }),
      },
      {
        headers: {
          "session-uid": sessionUid,
          "user-uid": userUid,
        },
      }
    );

    const result = withUIDirectives(response.data);

    if (result?.status === 'Success') {
      openNotification({
        type: "success",
        message: isEdit ? "News updated successfully" : "News added successfully",
      });
      onSuccess();
      onClose();
      SessionExtend();
    } else {
      openNotification({
        type: "error",
        message: result?.message || (isEdit ? "Failed to update news" : "Failed to add news"),
      });
    }
  } catch (error: any) {
    openNotification({
      type: "error",
      message: error?.response?.data?.message || (isEdit ? "Failed to update news" : "Add news failed, please retry"),
    });
  } finally {
    setIsSubmitting(false);
  }
};



  return (
    <ModalOverlay>
      <ModalContent>
        <h2 style={{marginTop:"0px",textAlign:"center"}}>{isEdit ? "Edit News" : "Add New News"}</h2>
        {formErrors.title && <p style={{ color: 'red', margin: 0 }}>* Title is required</p>}
        <DateLabel> Enter News title <Required>*</Required></DateLabel>
        <input type="text" placeholder="Title" value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} />
        {formErrors.description && <p style={{ color: 'red', margin: 0 }}>* Description is required</p>}
        <DateLabel> Enter Description about the news <Required>*</Required> </DateLabel>
        <textarea placeholder="Description" value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} />
        <DateLabel> Upload News Image ( Image of 880px * 380px prefered)<Required>*</Required></DateLabel>
        <input type="file" accept="image/*" onChange={handleImageUpload} />
        {imageBase64 && (
  <WidePreviewWrapper>
    <PreviewTitle>Image Preview (2.32:1)</PreviewTitle>
    <WideImagePreview
      src={`data:image/jpeg;base64,${imageBase64}`}
      alt="Preview"
    />
  </WidePreviewWrapper>
)}
<DateTimeRow>
  <DateTimeColumn>
    <DateLabel>Start Date <Required>*</Required></DateLabel>
    <Flatpickr
      value={fromDate}
      onChange={([date]) => {
        setFromDate(date);
        // Reset toDate if it becomes invalid
        if (toDate && date && toDate < date) {
          setToDate(undefined);
        }
      }}
      options={{
        dateFormat: "Y-m-d",
        minDate: "today", 
        position: "auto",           
    appendTo: document.body,    
    allowInput: true,
    clickOpens: true,
    disableMobile: false  
      }}
    />
  </DateTimeColumn>

  <DateTimeColumn>
    <DateLabel>Start Time <Required>*</Required></DateLabel>
    <TimePicker
  use12Hours
  format="h:mm A"
  value={fromTime}
  onChange={(val) => setFromTime(val)}
  className="custom-time-picker"
  style={{ width: "106%", height:"39px" }}
  placeholder="Select time"
  getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
/>

  </DateTimeColumn>
</DateTimeRow>

<DateTimeRow>
  <DateTimeColumn>
    <DateLabel>End Date <Required>*</Required></DateLabel>
    <Flatpickr
      value={toDate}
      onChange={([date]) => setToDate(date)}
      options={{
        dateFormat: "Y-m-d",
        minDate: fromDate || "today", 
        position: "auto",
    appendTo: document.body,
    allowInput: true,
    clickOpens: true,
    disableMobile: false
      }}
    />
  </DateTimeColumn>

  <DateTimeColumn>
    <DateLabel>End Time <Required>*</Required></DateLabel>
      <TimePicker
  use12Hours
  format="h:mm A"
  value={toTime}
  onChange={(val) => setToTime(val)}
  className="custom-time-picker"
  style={{ width: "106%", height:"39px" }}
  placeholder="Select time"
 getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}

/>

  </DateTimeColumn>
</DateTimeRow>



        <ButtonRow>
         <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            backgroundColor: isSubmitting ? "#ccc" : "#094752",
            cursor: isSubmitting ? "not-allowed" : "pointer"
          }}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
          <button onClick={onClose}>Cancel</button>
        </ButtonRow>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AddNewsModal;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99;
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 24px;
  border-radius: 8px;
  width: 400px;
  max-height: 80vh;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 12px;

  h2 {
    margin-bottom: 8px;
  }

  input {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
  }

  textarea {
    width: 100%;
    min-height: 30px; 
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
  }
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 6px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #aaa;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 5px;

  button {
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    background: #094752;
    color: #fff;
    font-size: 14px;
    cursor: pointer;

    &:last-child {
      background: #6c757d;
    }
  }
`;
const DateLabel = styled.div`
  font-size: 0.85rem;
  color: #333;
  margin-bottom: 4px;
  font-weight: 500;
`;
const PreviewTitle = styled.div`
  margin-bottom: 6px;
  font-size: 0.85rem;
  color: #222;
  font-weight: 500;
`;
const WidePreviewWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 43.1%;
  background: #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 10px;
`;

const WideImagePreview = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;
const DateTimeRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 25px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const DateTimeColumn = styled.div`
  flex: 1;
  min-width: 160px;

  input, .flatpickr-input {
    width: 100%;
  }
`;
const StyledTimePicker = styled(TimePicker)`
  .ant-picker-input > input {
    font-size: 14px;
  }
`;

const Required = styled.span`
  color: red;
  margin-left: 2px;
  font-weight: bold;
`;